import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonService, UserService } from '@tw/services';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridApi, themeQuartz } from 'ag-grid-community';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { Checkbox } from 'primeng/checkbox';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Dialog } from 'primeng/dialog';
import { FloatLabel } from 'primeng/floatlabel';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { LinkCellComponent } from 'src/shared/components/aggrid-cells/link-cell.component';
import { ToggleCellComponent } from 'src/shared/components/aggrid-cells/toggle-cell.component';
import {
  PermissionAction,
  PermissionSubject,
} from 'src/shared/models/permissions';
import { ClientService } from 'src/shared/services/client.service';
import { ProjectsService } from 'src/shared/services/projects.services';

type Client = {
  id: number;
  name: string;
  pocemail: string;
  pocname: string;
  isActive: true;
  createdBy: string;
  modifiedBy: string;
};

@Component({
  selector: 'tw-clients',
  imports: [
    Button,
    Dialog,
    Checkbox,
    ReactiveFormsModule,
    InputText,
    AgGridAngular,
    CommonModule,
    IconField,
    InputIcon,
    FloatLabel,
    FormsModule,
    ConfirmDialog,
    ToastModule,
    CardModule,
    TableModule,
    SelectButtonModule,
  ],
  providers: [ConfirmationService, MessageService],
  templateUrl: './clients.component.html',
  styleUrl: './clients.component.scss',
})
export class ClientsComponent implements OnInit {
  private userService = inject(UserService);
  private commonService = inject(CommonService);
  private formBuilder = inject(FormBuilder);
  popupTableData: any[] = [];
  selectedAction = '';
  showDialog = false;
  clientForm!: FormGroup;
  clients: Client[] = [];
  /** Full, unfiltered set; `clients` is the active/inactive-filtered view bound to the grid. */
  allClients: Client[] = [];

  statusFilter: 'all' | 'active' | 'inactive' = 'active';
  readonly statusOptions = [
    { label: 'All', value: 'all' },
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
  ];
  isEdit = false;
  submitted = false;
  searchTerm = '';

  readonly theme = themeQuartz.withParams({
    fontSize: 12,
    headerFontWeight: 600,
    rowHeight: 40,
    headerHeight: 36,
  });

  columnDefs: ColDef[] = [];
  readonly defaultColDef: ColDef = {
    resizable: true,
    sortable: true,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    suppressMovable: true,
    autoHeight: true,
    wrapText: true,
    flex: 1,
    minWidth: 120,
    tooltipValueGetter: (p) => (p.value == null ? '' : String(p.value)),
  };

  private gridApi?: GridApi;

  get CanAddClient() {
    return this.userService.hasPermission(
      PermissionSubject.Client,
      PermissionAction.Add,
    );
  }

  get CanEditClient() {
    return this.userService.hasPermission(
      PermissionSubject.Client,
      PermissionAction.Edit,
    );
  }

  constructor(
    private clientService: ClientService,
    private cdr: ChangeDetectorRef,
    private confirmationService: ConfirmationService,
    private projectService: ProjectsService,
  ) {}

  async ngOnInit() {
    this.initializeForm();
    this.buildColumnDefs();
    this.loadClients();
  }

  initializeForm() {
    this.clientForm = this.formBuilder.group({
      id: [null],
      name: ['', Validators.required],
      pocemail: ['', [Validators.email]],
      pocname: [''],
      isActive: [true],
    });
  }

  private buildColumnDefs(): void {
    const dashFormatter = (p: { value: any }) => p.value || '-';
    this.columnDefs = [
      {
        field: 'name',
        headerName: 'Name',
        cellRenderer: LinkCellComponent,
        cellRendererParams: {
          canClick: this.CanEditClient,
          onClick: (row: Client) => this.showDetails(row),
        },
        minWidth: 180,
      },
      {
        field: 'pocemail',
        headerName: 'POC Email',
        valueFormatter: dashFormatter,
        minWidth: 180,
      },
      {
        field: 'pocname',
        headerName: 'POC Name',
        valueFormatter: dashFormatter,
        minWidth: 140,
      },
      {
        field: 'isActive',
        headerName: 'Active',
        cellRenderer: ToggleCellComponent,
        cellClass: '!flex !items-center !justify-center',
        cellRendererParams: {
          canEdit: this.CanEditClient,
          onToggle: (row: Client, newValue: boolean) => {
            this.onToggleChange(row.isActive, newValue, row);
          },
        },
        // Filter on a stringified boolean so the floating text filter accepts "yes"/"no".
        filterValueGetter: (p) => (p.data?.isActive ? 'Yes' : 'No'),
        sortable: true,
        minWidth: 110,
        maxWidth: 130,
      },
      {
        field: 'createdBy',
        headerName: 'Created By',
        valueFormatter: dashFormatter,
        minWidth: 130,
      },
      {
        field: 'modifiedBy',
        headerName: 'Modified By',
        valueFormatter: dashFormatter,
        minWidth: 130,
      },
    ];
  }

  async loadClients() {
    this.allClients = await this.clientService.getClient();
    this.applyStatusFilter();
  }

  /** Re-derive the grid's row data from `allClients` based on the active/inactive toggle. */
  applyStatusFilter(): void {
    if (this.statusFilter === 'active') {
      this.clients = this.allClients.filter((c) => c.isActive);
    } else if (this.statusFilter === 'inactive') {
      this.clients = this.allClients.filter((c) => !c.isActive);
    } else {
      this.clients = this.allClients;
    }
  }

  exportToExcel(): void {
    const rows = this.clients.map((c) => ({
      ...c,
      isActive: c.isActive ? 'Yes' : 'No',
    }));

    this.commonService.exportExcelWithColors(
      rows,
      [
        { field: 'name', header: 'Name' },
        { field: 'pocemail', header: 'POC Email' },
        { field: 'pocname', header: 'POC Name' },
        { field: 'isActive', header: 'Active' },
        { field: 'createdBy', header: 'Created By' },
        { field: 'modifiedBy', header: 'Modified By' },
      ],
      { sheetName: 'Clients', fileName: 'Clients.xlsx' },
    );
  }

  onGridReady(event: { api: GridApi }): void {
    this.gridApi = event.api;
  }

  onSearch(value: string): void {
    this.searchTerm = value;
    this.gridApi?.setGridOption('quickFilterText', value);
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.gridApi?.setFilterModel(null);
    this.gridApi?.setGridOption('quickFilterText', '');
  }

  AddClient() {
    this.isEdit = false;
    this.showDialog = true;
    this.clientForm.reset();
    this.clientForm.patchValue({ isActive: true });
  }

  showDetails(client: Client) {
    if (!this.CanEditClient) return;
    this.isEdit = true;
    this.clientForm.patchValue(client);
    this.showDialog = true;
    this.cdr.detectChanges();
  }

  async updateStatus(client: any) {
    await this.clientService.updateClient(client.id, client);
  }

  async submit() {
    this.submitted = true;
    const formData = this.clientForm.getRawValue();
    if (!this.commonService.validateForm(this.clientForm)) {
      this.commonService.showToast(
        'Error',
        'Please fill all the required values!',
      );
      return;
    }
    try {
      const newClient = { ...formData };
      if (this.isEdit) {
        await this.clientService.updateClient(
          this.clientForm.value.id,
          newClient,
        );
        this.commonService.showToast('Success', 'Client updated successfully!');
      } else {
        await this.clientService.createClient(newClient);
        this.commonService.showToast('Success', 'Client created successfully!');
      }
      this.clientForm.reset();
      this.submitted = false;
      this.initializeForm();
      this.showDialog = false;
      this.loadClients();
      this.cdr.detectChanges();
    } catch (error: any) {
      error.error.errors.forEach((element: any) => {
        this.commonService.showToast('Error', element);
      });
    }
  }
  async onToggleChange(isActive: boolean, newValue: boolean, client: any) {
    this.selectedAction = newValue ? 'activate' : 'deactivate';

    this.popupTableData = [];

    if (isActive) {
      const response = await this.projectService.getProjects(true, client.id);
      this.popupTableData = this.chunkArray(response, 3);
    }

    this.confirmationService.confirm({
      header: `Confirm ${this.selectedAction}`,
      message: '',

      acceptLabel:
        this.selectedAction === 'activate' ? 'Activate' : 'Deactivate',
      rejectLabel: 'Cancel',

      acceptButtonStyleClass:
        this.selectedAction === 'activate'
          ? 'p-button-success'
          : 'p-button-danger',

      rejectButtonStyleClass: 'p-button-outlined p-button-secondary',

      accept: async () => {
        client.isActive = newValue;
        await this.clientService.updateClient(client.id, client);

        this.commonService.showToast(
          'Success',
          `Client ${this.selectedAction}d successfully!`,
        );

        await this.loadClients();
      },

      reject: () => {
        client.isActive = isActive;
        this.gridApi?.refreshCells({ force: true });
      },
    });
  }
  chunkArray(arr: any[], size: number): any[][] {
    const chunked = [];
    for (let i = 0; i < arr.length; i += size) {
      const chunk = arr.slice(i, i + size);
      while (chunk.length < size) {
        chunk.push({});
      }
      chunked.push(chunk);
    }
    return chunked;
  }
}
